<script setup>
import {
  ref,
  computed,
  watch
} from 'vue'
import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSnsAccountsStore } from '../stores/companies/sns/accounts'
import {
  KkButton,
  KkLoading,
} from '../components/base'
import {
  KkUpperTabLayout,
} from '../components/projects'
import {
  KkPostsBase,
  KkSelectAccountsModal,
  KkActionModal,
  KkLoadingModal,
  KkRequiredSnsConnection
} from '../components/domains/sns'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const snsAccountsStore = useSnsAccountsStore()
const {
  loading: snsAccountsLoading,
  data: snsAccounts,
} = storeToRefs(snsAccountsStore)
const { readSnsAccounts } = snsAccountsStore
const hasAvailableSnsApprovalFlow = computed(() => store.getters['companiesCompany/hasAvailableSnsApprovalFlow'])
const post = computed(() => store.getters['companiesStoreSnsPosts/post'])
const snsPostsLoading = computed(() => store.getters['companiesStoreSnsPosts/postLoading'])
const postSnsTypes = computed(() => store.getters['companiesStoreSnsPosts/postSnsTypes'])
const selectedPostSnsType = computed(() => store.getters['companiesStoreSnsPosts/selectedPostSnsType'])
const submitActionModalType = computed(() => store.getters['companiesStoreSnsPosts/submitActionModalType'])
const reservationDateTime = computed(() => store.getters['companiesStoreSnsPosts/reservationDateTime'])
const showOnlyOwnerAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const approvalRequestNotes = computed(() => store.getters['companiesStoreSnsPosts/approvalRequestNotes'])
const postAccounts = computed(() => store.getters['companiesStoreSnsPosts/postAccounts'])
const postTiming = computed(() => store.getters['companiesStoreSnsPosts/postTiming'])

const getPost = (payload) => store.dispatch('companiesStoreSnsPosts/getPost', payload)
const getDraftedPost = (payload) => store.dispatch('companiesStoreSnsPosts/getDraftedPost', payload)
const getRequestingPost = (payload) => store.dispatch('companiesStoreSnsPosts/getRequestingPost', payload)
const createPost = (payload) => store.dispatch('companiesStoreSnsPosts/createPost', payload)
const updatePost = (payload) => store.dispatch('companiesStoreSnsPosts/updatePost', payload)
const createPostAfterRequesting = (payload) => store.dispatch('companiesStoreSnsPosts/createPostAfterRequesting', payload)
const createPostAfterDraft = (payload) => store.dispatch('companiesStoreSnsPosts/createPostAfterDraft', payload)
const createDraftedPost = (payload) => store.dispatch('companiesStoreSnsPosts/createDraftedPost', payload)
const updateDraftedPost = (payload) => store.dispatch('companiesStoreSnsPosts/updateDraftedPost', payload)
const createRequestingPost = (payload) => store.dispatch('companiesStoreSnsPosts/createRequestingPost', payload)
const updateRequestingPost = (payload) => store.dispatch('companiesStoreSnsPosts/updateRequestingPost', payload)
const createRequestingPostAfterDraft = (payload) => store.dispatch('companiesStoreSnsPosts/createRequestingPostAfterDraft', payload)
const readSnsCategories = () => store.dispatch('companiesSnsCategories/readSnsCategories')

const readCompany = (companyUuid) => store.dispatch('companiesCompany/readCompany', companyUuid)
const updatePostSnsType = (payload) => store.dispatch('companiesStoreSnsPosts/updatePostSnsType', payload)
const updateSelectedPostSnsType = (payload) => store.dispatch('companiesStoreSnsPosts/updateSelectedPostSnsType', payload)
const updatePostAccounts = (payload) => store.dispatch('companiesStoreSnsPosts/updatePostAccounts', payload)
const resetPost = () => store.dispatch('companiesStoreSnsPosts/resetPost')
const updatePostTiming = (timing) => store.dispatch('companiesStoreSnsPosts/updatePostTiming', timing)
const noLinked = ref(false)
const showAccountsModal = ref(false)
const showActionModal = ref(false)
const showLoadingModal = ref(false)
const loadingCompleted = ref(false)
const loadingFailed = ref(false)
const paramLoading = ref(false)

const isPostEditMode = computed(() => !!route.params.post_id)
const isPostCopyMode = computed(() => !!route.query.copy)
const isDraftedPostEditMode = computed(() => !!route.params.drafted_post_id)
const isDraftedPostCopyMode = computed(() => !!route.query.drafted_post_copy)
const isRequestingPostEditMode = computed(() => !!route.params.requesting_post_id)
const isRequestingPostOwnerEditMode = computed(() => {
  // 申請中の投稿をオーナー権限のユーザーが編集する場合（申請中 or リジェクト中かどうかに関わらず）
  return isRequestingPostEditMode.value && showOnlyOwnerAuthority.value
})
const isRequestingPostCopyMode = computed(() => !!route.query.requesting_post_copy)

const currentCompanyUuid = computed(() => route.params.company_uuid)
const loading = computed(() => snsPostsLoading.value || snsAccountsLoading.value || paramLoading.value)
const needApproval = computed(() => hasAvailableSnsApprovalFlow.value && !showOnlyOwnerAuthority.value)
const accounts = computed(() => {
  if(selectedPostSnsType.value === 'MULTI') return snsAccounts.value
  return snsAccounts.value.filter(v => v.kind === selectedPostSnsType.value.toLowerCase())
})

const menuList = computed(()=> {
  const menuList = {}
  for(const key in postSnsTypes.value) {
    menuList[key] = postSnsTypes.value[key]
  }
  return menuList
})

const canPostFaceBook = computed(() => selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'FACEBOOK')
const canPostInstagram = computed(() => selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'INSTAGRAM' )
const canPostTwitter = computed(() => selectedPostSnsType.value === 'MULTI' || selectedPostSnsType.value === 'TWITTER' )

const clickMenu = async(key) => {
  updatePostSnsType(key)
  updateSelectedPostSnsType(key)
}
watch(
  route, () => load()
)

const initParams = async () => {
  paramLoading.value = true
  resetPost()
  updatePostTiming('reserve')
  await Promise.all([
    readSnsAccounts({ companyUuid: currentCompanyUuid.value }),
    readSnsCategories(),
    readCompany(currentCompanyUuid.value)
  ])
  if (snsAccounts.value.length === 0) noLinked.value = true
  if (isPostEditMode.value) {
    const payload = {
      companyUuid: currentCompanyUuid.value,
      id: route.params.post_id,
    }
    await getPost(payload)
  } else if (isPostCopyMode.value) {
    const payload = {
      companyUuid: currentCompanyUuid.value,
      id: route.query.copy,
      isCopied: true
    }
    await getPost(payload)
  } else if (isDraftedPostEditMode.value) {
    const payload = {
      id: route.params.drafted_post_id,
    }
    await getDraftedPost(payload)
  } else if (isDraftedPostCopyMode.value) {
    const payload = {
      id: route.query.drafted_post_copy,
      isCopied: true
    }
    await getDraftedPost(payload)
  } else if (isRequestingPostEditMode.value) {
    const payload = {
      id: route.params.requesting_post_id,
    }
    await getRequestingPost(payload)
  } else if (isRequestingPostCopyMode.value) {
    const payload = {
      id: route.query.requesting_post_copy,
      isCopied: true
    }
    await getRequestingPost(payload, true)
  }

  if (post.value?.kind) {
    updatePostSnsType(post.value.kind.toUpperCase())
    updateSelectedPostSnsType(post.value.kind.toUpperCase())
  } else {
    updatePostSnsType('MULTI')
    updateSelectedPostSnsType('MULTI')
  }
  paramLoading.value = false
}

const createPayloadForPost = (reservationDateAndTime = null) => {
  let imagesPayload = []
  if (post.value.images.length) {
    imagesPayload = post.value.images.map(image => {
      const payload = {
        filename: image.filename,
        content : image.content
      }
      return payload
    })
  }
  let moviePayload = null
  if (post.value.movie) {
    moviePayload = {
      filename: post.value.movie.filename,
      content : post.value.movie.content
    }
  }
  let facebookPagesPayload = []
  if (post.value.facebookPages.length) {
    facebookPagesPayload = post.value.facebookPages.map(page => {
      const payload = {
        id: page.id,
      }
      return payload
    })
  }
  let instagramBusinessAccountsPayload = []
  if (post.value.instagramBusinessAccounts.length) {
    instagramBusinessAccountsPayload = post.value.instagramBusinessAccounts.map(account => {
      const payload = {
        id: account.id,
      }
      return payload
    })
  }
  let twitterUserProfilesPayload = []
  if (post.value.twitterUserProfiles.length) {
    twitterUserProfilesPayload = post.value.twitterUserProfiles.map(profile => {
      const payload = {
        id: profile.id,
      }
      return payload
    })
  }
  const postPayload = {
    title: post.value.title,
    category_id: post.value.categoryId,
    post_images: imagesPayload,
    post_movie: moviePayload,
    facebook_pages: canPostFaceBook.value ? facebookPagesPayload : [],
    instagrams: canPostInstagram.value ? instagramBusinessAccountsPayload : [],
    twitters: canPostTwitter.value ? twitterUserProfilesPayload : [],
    text: post.value.text,
  }
  if (reservationDateAndTime) postPayload['scheduled_at'] = reservationDateTime.value.toISOString()
  if (submitActionModalType.value !== 'draft' && postTiming.value === 'immediate') postPayload['is_immediate'] = true

  return postPayload
}

const openAccountsModal = () => {
  showAccountsModal.value = !showAccountsModal.value
}

const closeAccountsModal = () => {
  showAccountsModal.value = false
}

const updateSelectedAccounts = (ids) => {
  const results = accounts.value.filter(account => {
    return ids.includes(`${account.type}-${account.id}`)
  })
  updatePostAccounts(results)
  closeAccountsModal()
}

const openActionModal = async() => {
  const postPayload = createPayloadForPost(reservationDateTime.value)

  const payload = {}
  if(isPostEditMode.value){
    payload['post_id'] = route.params.post_id
  }

  if(submitActionModalType.value === 'draft'){
    showActionModal.value = !showActionModal.value
  } else {
    if (!post.value.facebookPages.length && !post.value.instagramBusinessAccounts.length && !post.value.twitterUserProfiles.length) {
      alert(t('pages.CompaniesStoreSnsPost.form.validation.accountsRequired'))
      return
    }
    if (needApproval.value) {
      // 申請経由のパターン
      payload['requesting_post'] = postPayload
      payload['requesting_post_id'] = route.params.drafted_post_id
      showActionModal.value = !showActionModal.value
    } else {
      // オーナーが投稿する or 承認申請機能が有効でない
      payload['post'] = postPayload
      showActionModal.value = !showActionModal.value
    }
  }
}

const closeActionModal = () => {
  showActionModal.value = false
}

const closeLoadingModal = () => {
  showLoadingModal.value = !showLoadingModal.value
  loadingCompleted.value = false
  loadingFailed.value = false
}


const actionModalSubmit = () => {
  switch (submitActionModalType.value) {
  case 'post':
    newPostSubmit(reservationDateTime.value)
    break
  case 'draft':
    newDraftedPostSubmit(reservationDateTime.value)
    break
  case 'request':
    newRequestingPostSubmit(reservationDateTime.value)
    break
  }
}

const newPostSubmit = async (reservationDateTime) => {
  closeActionModal()

  showLoadingModal.value = true

  const postPayload = createPayloadForPost(reservationDateTime)

  let isSuccess
  if(isPostEditMode.value){
    isSuccess = await updatePost({payload: { sns_post: postPayload }, id: post.value.id})
  } else if(isRequestingPostOwnerEditMode.value) {
    isSuccess = await createPostAfterRequesting({payload: { sns_post: postPayload }, id: post.value.id})
  } else if(isDraftedPostEditMode.value) {
    isSuccess = await createPostAfterDraft({payload: { sns_post: postPayload }, draftedPostId: post.value.id})
  } else {
    isSuccess = await createPost({sns_post: postPayload})
  }

  if (isSuccess) {
    loadingCompleted.value = true
    setTimeout(
      () => {
        router.push({ path: '/s/companies/' + currentCompanyUuid.value + '/sns/posts' })
      },
      3000
    )
  } else {
    loadingFailed.value = true
  }
}

const newDraftedPostSubmit = async (reservationDateTime) => {
  closeActionModal()

  showLoadingModal.value = true
  const postPayload = createPayloadForPost(reservationDateTime)

  const isSuccess = isDraftedPostEditMode.value ? await updateDraftedPost({ payload: { sns_post: postPayload }, id: post.value.id }) : await createDraftedPost({
    sns_post: postPayload
  })

  if (isSuccess) {
    loadingCompleted.value = true
    setTimeout(
      () => {
        router.push({ path: '/s/companies/' + currentCompanyUuid.value + '/sns/posts' })
      },
      3000
    )
  } else {
    loadingFailed.value = true
  }
}

const newRequestingPostSubmit = async (reservationDateTime) => {
  closeActionModal()

  showLoadingModal.value = true

  const postPayload = createPayloadForPost(reservationDateTime)


  let isSuccess
  if(isRequestingPostEditMode.value) {
    isSuccess = await updateRequestingPost({
      payload: { sns_post: postPayload, comment: approvalRequestNotes.value ?? '' },
      id: post.value.id,
    })
  } else if (isDraftedPostEditMode.value) {
    isSuccess = await createRequestingPostAfterDraft({
      payload: { sns_post: postPayload, comment: approvalRequestNotes.value ?? '' },
      drafted_post_id: route.params.drafted_post_id,
    })
  } else {
    isSuccess = await createRequestingPost({
      sns_post: postPayload,
      comment: approvalRequestNotes.value
    })
  }

  if (isSuccess) {
    loadingCompleted.value = true
    setTimeout(
      () => {
        router.push({ path: '/s/companies/' + currentCompanyUuid.value + '/sns/posts' })
      },
      3000
    )
  } else {
    loadingFailed.value = true
  }
}

const load = async () => {
  initParams()
}

load()

</script>
<template>
  <div>
    <div v-if="noLinked">
      <KkRequiredSnsConnection />
    </div>
    <KkUpperTabLayout
      v-else
      :menu-list="menuList"
      :selected-tab-key="selectedPostSnsType"
      @click-menu="clickMenu($event)"
    >
      <template #nav-link>
        <KkButton
          color="secondary"
          class="button"
          :to="{ name: 'CompaniesStoreSnsPostsIndex', params: { company_uuid: currentCompanyUuid }}"
          :text="t('labels.backToList')"
        />
      </template>
      <KkLoading v-if="loading" />
      <KkPostsBase
        v-else
        :is-post-edit="isPostEditMode"
        :need-approval="needApproval"
        @open-action-modal="openActionModal"
        @open-accounts-modal="openAccountsModal"
      />

      <KkSelectAccountsModal
        v-if="showAccountsModal"
        :accounts="accounts"
        :open-modal="showAccountsModal"
        :check-ids="postAccounts.map(account => `${account.type}-${account.id}`)"
        @submit="updateSelectedAccounts"
        @close="closeAccountsModal"
      />

      <KkActionModal
        v-if="!loading && showActionModal"
        :show-action-modal="showActionModal"
        @close-action-modal="closeActionModal"
        @new-post-submit="actionModalSubmit"
      />

      <KkLoadingModal
        :completed="loadingCompleted"
        :failed="loadingFailed"
        :show-loading-modal="showLoadingModal"
        @close-loading-modal="closeLoadingModal()"
      />
    </KkUpperTabLayout>
  </div>
</template>

<style lang="scss" scoped>
.button {
  @media screen and (max-width: 768px) {
    margin-left: auto;
    margin-bottom: 20px;
  }
}

</style>
